import { Client } from "@/Common/Api/Client";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { LicenseDto } from "@/Common/Api/Labs/Dto/Responses/LicenseDto";
import { DrugInteractionAlertsFilterOption } from "@/Common/Models/Employee/Enums/DrugInteractionAlertsFilterOption";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { RAMQCurrentUser } from "@/Common/Models/RAMQ/RAMQCurrentUser";
import { RAMQDoctor } from "@/Common/Models/RAMQ/RAMQDoctor";
import { UserSettingsConfig } from "@/Common/Models/Settings/UserSettingsConfig";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { UserSettings } from "@/Globals/Models/UserSettings";

class SettingsAction extends BaseAction {
    public ChangeLanguage(lang: string): void {
        const requestData = {
            LanguageCode: lang,
            UserId: sessionStore.userId
        };

        this.Execute(ServerOperations.Server.Configuration_ChangeLanguage, requestData);
    }

    public ChangeDefaultUnit(): void {
        const requestData = {
            UseMetricSystem: sessionStore.useMetricSystemAsDefault
        };

        this.Execute(ServerOperations.Server.Configuration_ChangeUnit, requestData);
    }

    public changeDocumentView(isDocumentInAppView: boolean): void {
        const userSettings = new UserSettings({
            UserSettingsConfig: new UserSettingsConfig(cacheStore.UserSettings.UserSettingsConfig)
        });

        userSettings.UserSettingsConfig.ShowDocumentInAppView = isDocumentInAppView;

        this.Execute(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        cacheStore.UserSettings.UserSettingsConfig = userSettings.UserSettingsConfig;
    }

    public SaveSignatureImage(path: string): void {
        this.Execute(ServerOperations.Server.Myle_Settings_SaveSignFileName, {
            Path: path
        });
    }

    public changeSite(siteId: string): void {
        const requestData = {
            SiteId: siteId,
            EmployeeId: sessionStore.individualContactId
        };

        this.Execute(ServerOperations.Server.Myle_ChangeEmployeeSite, requestData);
    }

    public UpdateEmployeeSettings(request: Partial<IUpdateEmployeeSettings>, callback: (response) => void): void {
        request.EmployeeId = sessionStore.individualContactId;

        Client.Post(ServerOperations.Server.Configuration_UpdateEmployeeSettings, request, response => callback(response));
    }

    public UpdateAutoResponders(isInVacation: boolean, from?: Date, to?: Date, note?: string): void {
        const requestData = { EmployeeId: sessionStore.individualContactId, From: from, To: to, Note: note, IsInVacation: isInVacation };
        if (from == null) { delete requestData.From; }
        if (to == null) { delete requestData.To; }
        if (note == null) { delete requestData.Note; }
        this.Execute(ServerOperations.Server.Configuration_UpdateAutoResponder, requestData);
    }

    public RemoveSignatureFile(): void {
        this.Execute(ServerOperations.Server.Configuration_RemoveSignatureImage, { EmployeeId: sessionStore.individualContactId });
    }

    public LoadSignatureImage(): void {
        const requestData = { EmployeeId: sessionStore.individualContactId };
        this.Execute(ServerOperations.Server.Myle_Settings_LoadSignatureByteArray, requestData);
    }

    public LoadSharingSettings(): void {
        const requestData = { EmployeeId: sessionStore.individualContactId };
        this.Execute(ServerOperations.Server.Configuration_GetEmployeeSharing, requestData);
    }

    public LoadPdfTagsCache(): void {
        this.Execute(ServerOperations.Server.LoadPDFTags, null, null, true);
    }

    public UpdateSharingSettings(
        messageClinicianIds?: string[],
        taskClinicianIds?: string[],
        externalCommunicationsClinicians?: string[],
        portalCommunicationsClinicians?: string[]
    ): void {
        const requestData = {
            EmployeeId: sessionStore.individualContactId,
            MessageClinicians: null,
            TaskClinicians: null,
            ExternalCommunicationsClinicians: null,
            PortalCommunicationsClinicians: null
        };

        if (messageClinicianIds != null)
            requestData.MessageClinicians = messageClinicianIds;

        if (taskClinicianIds != null)
            requestData.TaskClinicians = taskClinicianIds;

        if (externalCommunicationsClinicians != null)
            requestData.ExternalCommunicationsClinicians = externalCommunicationsClinicians;

        if (portalCommunicationsClinicians != null)
            requestData.PortalCommunicationsClinicians = portalCommunicationsClinicians;

        this.Execute(ServerOperations.Server.Configuration_UpdateEmployeeSharing, requestData);
    }

    public UpdatePrintingSettings(isPortraitOrientation: boolean, largePrinting: boolean, isPrescriptionPrinterRegular: boolean, prescriptionPrintType: PrescriptionPrintType, isAppointmentConfirmationPrinterRegular: boolean): void {
        const requestData = {
            EmployeeId: sessionStore.individualContactId,
            ArePortraitLabels: isPortraitOrientation,
            IsLargePrinting: largePrinting,
            IsPrescriptionPrinterRegular: isPrescriptionPrinterRegular,
            PrescriptionPrintType: prescriptionPrintType,
            IsAppointmentPrinterRegular: isAppointmentConfirmationPrinterRegular
        };

        this.Execute(ServerOperations.Server.Configuration_UpdateEmployeePrintingSettings, requestData);
    }

    public UpdatePdfTagsForEmployee(tags: string[]): void {
        const requestData = {
            EmployeeId: sessionStore.individualContactId,
            Tags: tags
        };
        this.Execute(ServerOperations.Server.Configuration_UpdateEmployeePdfTags, requestData);
    }

    public GetOrphanLabsForwards(licenseIds: LicenseDto[]): void {
        this.Execute(ServerOperations.Server.ARLMYLE_GetOrphanLabForwards, { LicenseNumbers: licenseIds });
    }

    public SavePreferedFaxNumber(faxAccountNumberId: number): void {
        this.Execute(ServerOperations.Server.Myle_Settings_SavePreferedFaxNumber, { FaxAccountNumberId: faxAccountNumberId });
    }

    public SaveOrphanLabsClinician(id: string, siteContext: string, dateFrom?: Date, dateTo?: Date): void {
        const request = {
            Id: id,
            From: dateFrom,
            To: dateTo,
            SiteContext: siteContext
        };

        if (dateFrom == null) { delete request.From; }
        if (dateTo == null) { delete request.To; }

        this.Execute(ServerOperations.Server.ARLMYLE_SaveOrphanLabForwards, request);
    }

    public RemoveOrphanLabClinician(id: string): void {
        this.Execute(ServerOperations.Server.ARLMYLE_RemoveClinicianFromOrphanLabForwards, { Id: id });
    }

    public LoadPatientEnrollments(): void {
        const ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        const ramqDoctor = new RAMQDoctor();
        ramqDoctor.Class = "1";
        ramqDoctor.NoGMF = cacheStore.GetSiteById(sessionStore.getCurrentEmployee().getActiveSite().Model.ParentSiteId).Model.ClinicalCode;
        ramqDoctor.Licence = sessionStore.getCurrentEmployee().Model.FirstLicenceNumber;

        const requestData = {
            RAMQCurrentUser: ramqCurrentUser,
            RAMQDoctor: ramqDoctor
        };

        this.Execute(ServerOperations.Server.RAMQ_GetPatientsRegistration, requestData);
    }

    public LoadEmployeeEmails(): void {
        const requestData = {};
        this.Execute(ServerOperations.Server.Myle_Employee_Get_Emails, requestData);
    }

    public SaveEmployeeEmails(emails: Email[], isNotifiedByEmail: boolean, userId: string, password: string): void {
        const requestData = {
            Emails: emails,
            IsNotifiedByEmail: isNotifiedByEmail,
            UserId: userId,
            Password: password
        };
        this.Execute(ServerOperations.Server.Myle_Employee_Save_Emails, requestData);
    }

    public VerifyEmailAddress(emailAddress: string, contactPointId: string, verificationCode: string): void {
        const requestData = {
            Address: emailAddress,
            ContactPointId: contactPointId,
            VerificationCode: verificationCode
        };

        this.Execute(ServerOperations.Server.Myle_Verify_Email, requestData);
    }

    public ResendEmailVerificationCode(emailAddress: string, contactPointId: string): void {
        const requestData = {
            Address: emailAddress,
            ContactPointId: contactPointId
        };

        this.Execute(ServerOperations.Server.Myle_Employee_Resend_Verification_Code, requestData);
    }

    public changeActiveDepartment(siteId: string, departmentId: string): void {
        const requestData = {
            ActiveDepartmentId: departmentId,
            ActiveSiteId: siteId
        };

        this.Execute(ServerOperations.Server.Configuration_UpdateActiveDepartment, requestData);
    }

    /** @deprecated Current user code delivery method should be saved by {@link NewFlowMultifactorAuthenticationSettings} instead. */
    public UpdateAuthenticatorCodeDeliveryMethod(method: AuthenticatorCodeDeliveryMethod): void {
        const requestData = { AuthenticatorCodeDeliveryMethod: method };
        this.Execute(ServerOperations.Server.Myle_MultifactorAuthentication_AuthenticatorCodeDeliveryMethod, requestData);
    }
}

interface IUpdateEmployeeSettings {
    EmployeeId: string;
    ActivatePharmacotherapeuticAssistant: boolean;
    IsProblemVisibility: boolean;
    ShareLabResults: boolean;
    DefaultRefill: number;
    ShowDiagnosisCode: boolean;
    DisableTimers: boolean;
    BillingProvider: string;
    ShowTelemedWindowInMylePopup: boolean;
    ShouldShowAndCalculateMedicationDispensationQuantities: boolean;
    LongTermMedicationDurationInDays: number;
    LongTermMedicationDefaultRefillNumber: number;
    ShouldAttachMedicalSummaryWhenCreatingConsultationRequest: boolean;
    ShouldAttachMedicationSummaryWhenCreatingConsultationRequest: boolean;
    DrugInteractionAlertsFilterOption: DrugInteractionAlertsFilterOption;
    AllowPatientsToInitiatePortalMessages?: boolean;
    ShowAlertsAndSpecialNeedsInPrintedSummary: boolean;
    ShowPopupForNewAlertsAndSpecialNeeds: boolean;
}

export { SettingsAction };
